import { ReactNode, useCallback, useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "../../features/i18n/I18nProvider";
import "./Dialog.css";

interface DialogProps {
    isOpen: boolean;
    /** Names the dialog for screen readers and is shown as its heading. */
    title: string;
    onClose: () => void;
    children: ReactNode;
    size?: "md" | "lg";
    className?: string;
    placement?: "center" | "end";
}

const FOCUSABLE =
    'button:not([disabled]), a[href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The one general-purpose modal.
 *
 * WHY THIS EXISTS BESIDE `ConfirmModal`
 * `ConfirmModal` is a two-button question and carries its own safe-default
 * focus rule (Cancel, so a stray Enter cannot confirm). Anything else that
 * needs a modal - a video player, a picker sheet - used to mean hand-rolling
 * the same portal, focus trap and Escape handling, and getting one of them
 * wrong. This is that behaviour, once, for content that is not a question.
 *
 *   - `role="dialog"` + `aria-modal` + a title the reader hears
 *   - focus moves in on open, is trapped, and returns to the opener on close
 *   - Escape and a click on the backdrop close it
 *   - page scroll is locked while it is open
 *
 * `onClose` is held in a ref so a parent passing an inline arrow does not
 * re-run the effect on every render and yank focus back to the first control
 * mid-interaction.
 */
export const Dialog = ({ isOpen, title, onClose, children, size = "md", className, placement = "center" }: DialogProps) => {
    const panelRef = useRef<HTMLDivElement | null>(null);
    const returnFocusRef = useRef<HTMLElement | null>(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const titleId = useId();
    const { t } = useTranslation();

    const focusables = useCallback(
        (): HTMLElement[] =>
            panelRef.current ? Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)) : [],
        []
    );

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        returnFocusRef.current = document.activeElement as HTMLElement | null;
        (focusables()[0] ?? panelRef.current)?.focus();

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                event.preventDefault();
                onCloseRef.current();
                return;
            }
            if (event.key !== "Tab") {
                return;
            }
            const items = focusables();
            if (items.length === 0) {
                event.preventDefault();
                return;
            }
            const first = items[0];
            const last = items[items.length - 1];
            const active = document.activeElement as HTMLElement | null;
            if (event.shiftKey && (active === first || !panelRef.current?.contains(active))) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && active === last) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener("keydown", onKeyDown, true);
        return () => {
            document.removeEventListener("keydown", onKeyDown, true);
            document.body.style.overflow = previousOverflow;
            returnFocusRef.current?.focus?.();
        };
    }, [isOpen, focusables]);

    if (!isOpen) {
        return null;
    }

    return createPortal(
        <div
            className={`ui-dialog__backdrop${placement === "end" ? " ui-dialog__backdrop--end" : ""}`}
            onMouseDown={(event) => {
                // Only a press on the backdrop itself: a drag that starts inside
                // the panel and ends outside must not dismiss it.
                if (event.target === event.currentTarget) {
                    onCloseRef.current();
                }
            }}
        >
            <div
                className={`ui-dialog ui-dialog--${size}${className ? ` ${className}` : ""}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
                ref={panelRef}
            >
                <header className="ui-dialog__head">
                    <h2 id={titleId} className="ui-dialog__title">{title}</h2>
                    <button type="button" className="ui-dialog__close" aria-label={t("common.close")} onClick={() => onCloseRef.current()}>
                        <span aria-hidden="true">×</span>
                    </button>
                </header>
                <div className="ui-dialog__body">{children}</div>
            </div>
        </div>,
        document.body
    );
};
